import { useContext, useMemo, useState } from 'react';
import { Formik } from 'formik';
import NumberFormat from 'react-number-format';
import useSWR from 'swr';
import APIMethodKeys from '../../../client/APIMethodKeys';
import { GlobalContext } from '../../contexts/GlobalContext';
import { formatRequestFiltersForAPI } from '../../hooks/useRequestList';
import { useIsQuotaEnforcementActive } from '../../hooks/useIsQuotaEnforcementActive';
import Button from '../base/Button';
import { StyledCard, StyledCardSection } from '../base/Card';
import Icon from '../base/Icon';
import Skeleton from '../base/Skeleton';
import Text from '../base/Text';
import Tooltip from '../base/Tooltip';
import { useDialog } from '../Dialog';
import Switch from '../Form/Fields/Switch';
import { Div } from '../helpers/StyledUtils';
import { useToasts } from '../Toast';
import BulkOperationBreakdown from './BulkOperationBreakdown';
import ReplayTargetFields, { ReplayTargetFormValues, formatReplayTarget, validateReplayTarget } from './ReplayTargetFields';
import { BulkOperationFilters } from './types';
import { cleanupQueryForBulkOperation, ensureDateFilters, getOperationLabels, hasNoFilters } from './utils';
interface Props {
  query: BulkOperationFilters;
  revalidateCounts: () => void;
  onSubmit?: () => void;
}
type Mode = 'retry' | 'replay';

/**
 * Unified submit form for the Requests bulk action. Retry (the default) resends
 * each matching request's existing events; toggling "Run as replay" re-ingests
 * each matching request through the pipeline onto a chosen target source and set
 * of connections. The shared conditions breakdown and count/action bar render
 * once — only the replay target fields are revealed by the toggle.
 */
const SubmitBulkRequestRetryReplay: React.FC<Props> = ({
  query,
  onSubmit,
  revalidateCounts
}) => {
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const [mode, setMode] = useState<Mode>('retry');
  const [pending, setPending] = useState(false);
  const {
    addToast
  } = useToasts();
  const is_quota_enforcement_active = useIsQuotaEnforcementActive();
  const showDialog = useDialog();
  const cleaned_query = useMemo(() => cleanupQueryForBulkOperation(query), [query]);
  const query_with_date = useMemo(() => ensureDateFilters(cleaned_query), [cleaned_query]);
  const api_query = useMemo(() => formatRequestFiltersForAPI(query_with_date as any, 'created_at'), [query_with_date]);

  // The eligible count differs per mode: retry counts resendable requests, replay
  // counts requests that can be re-ingested onto the target.
  const {
    data: plan,
    isLoading: is_plan_loading
  } = useSWR(mode === 'replay' ? APIMethodKeys.bulk.requestsReplay.plan({
    query: api_query
  }) : APIMethodKeys.bulk.requests.plan({
    query: api_query
  }), () => mode === 'replay' ? HookdeckAPI.bulk.requestsReplay.plan({
    query: api_query
  }) : HookdeckAPI.bulk.requests.plan({
    query: api_query
  }));
  const retry_labels = getOperationLabels('retry', 'requests');
  const no_filters = hasNoFilters(api_query);
  const submitRetry = () => {
    showDialog(() => {
      setPending(true);
      HookdeckAPI.bulk.requests.retry({
        query: api_query
      }).then(() => {
        addToast('success', retry_labels.success_message);
        setPending(false);
        revalidateCounts();
        onSubmit?.();
      }).catch(() => {
        addToast('error', retry_labels.error_message);
        setPending(false);
      });
    }, () => null, {
      title: retry_labels.dialog_title,
      message: <Div flex={{
        direction: 'column',
        gap: 4
      }}>
            <Text>{retry_labels.dialog_message}</Text>
            <BulkOperationBreakdown operation_type="retry" model="requests" query={api_query} />
          </Div>,
      footer_content: <Div h={100} flex={{
        align: 'center',
        justify: 'center'
      }}>
            {plan ? <Text bold>
                <NumberFormat renderText={v => v} displayType="text" value={plan?.estimated_count || 0} thousandSeparator={','} />{' '}
                {plan?.estimated_count === 1 ? 'request' : 'requests'}
              </Text> : <Skeleton h={{
          px: 20
        }} w={{
          px: 200
        }} loading />}
          </Div>,
      cancel_label: retry_labels.dialog_cancel_label,
      submit_label: retry_labels.dialog_submit_label
    });
  };
  const submitReplay = async (values: ReplayTargetFormValues) => {
    setPending(true);
    try {
      const operation = await HookdeckAPI.bulk.requestsReplay.create({
        query: {
          ...api_query,
          target: formatReplayTarget(values)
        }
      });
      addToast('success', `Bulk replay started (#${operation.number ?? operation.id})`);
      revalidateCounts();
      onSubmit?.();
    } catch {
      addToast('error', 'Failed to start bulk replay');
    } finally {
      setPending(false);
    }
  };
  return <Formik<ReplayTargetFormValues> initialValues={{
    source_id: '',
    connection_scope: 'all',
    webhook_ids: []
  }} validate={validateReplayTarget} validateOnMount onSubmit={submitReplay}>
      {({
      isValid,
      handleSubmit
    }) => <>
          <StyledCardSection p={4}>
            <Text m={{
          b: 4
        }}>
              Bulk {mode} all{' '}
              <Tooltip as="span" side="bottom" tooltip={mode === 'replay' ? 'Replay re-ingests each matching request through the pipeline as if it just arrived. A new request is created on the target source. Original requests and events are untouched.' : "Retry resends each matching request's existing events. Events with pending retries are excluded from the bulk retry count."}>
                <Text as="span" underline style={{
              cursor: 'default'
            }}>
                  {mode === 'replay' ? 'matching requests' : 'eligible requests'}
                </Text>
              </Tooltip>{' '}
              where:
            </Text>
            <BulkOperationBreakdown operation_type="retry" model="requests" query={api_query} />

            <StyledCard overflow_hidden dashed={mode === 'retry'} m={{
          t: 4
        }}>
              <StyledCardSection p={2}>
                <Div flex={{
              align: 'center',
              gap: 4
            }} p={1}>
                  <Switch checked={mode === 'replay'} onChange={e => setMode(e.target.checked ? 'replay' : 'retry')} />
                  <Text semi muted={mode === 'retry'} flex={{
                align: 'center'
              }}>
                    Run as replay
                    <Tooltip tooltip="Default is Retry — resends each matching request's existing events. Toggle Replay to re-ingest each matching request through the pipeline (creating new requests and events) and target a different set of connections.">
                      <Icon muted right small pointer icon="info" />
                    </Tooltip>
                  </Text>
                </Div>
              </StyledCardSection>
              <StyledCardSection borderless={mode === 'retry'} p={mode === 'replay' ? 4 : 0}>
                {mode === 'replay' && <ReplayTargetFields />}
              </StyledCardSection>
            </StyledCard>
          </StyledCardSection>

          <StyledCardSection p={{
        x: 4,
        y: 3
      }}>
            <Div flex={{
          justify: 'space-between',
          align: 'center'
        }}>
              {!no_filters ? <Text bold flex={{
            align: 'center',
            gap: 1
          }} shimmering={is_plan_loading}>
                  {is_plan_loading ? 'Loading' : <NumberFormat renderText={v_0 => v_0} displayType="text" value={plan?.estimated_count ?? 0} thousandSeparator={','} />}{' '}
                  request{plan?.estimated_count === 1 ? '' : 's'}
                  <Tooltip tooltip={mode === 'replay' ? 'Count of historical requests matching the filters above. Each one will be replayed through the selected target connection(s).' : "Count of requests eligible for bulk retry, given the conditions above. If this value doesn't match the total on your request list, Hookdeck has excluded requests ineligible for retry."}>
                    <Icon icon="info" p={0.5} />
                  </Tooltip>
                </Text> : <span />}

              <Tooltip tooltip={is_quota_enforcement_active ? `${mode === 'replay' ? 'Replay' : 'Retry'} disabled - Plan limit exceeded. Upgrade to enable.` : undefined} disabled={!is_quota_enforcement_active}>
                <Button.Permission role="member" outline onClick={mode === 'replay' ? () => handleSubmit() : submitRetry} disabled={plan?.estimated_count === 0 || pending || no_filters || is_quota_enforcement_active || mode === 'replay' && !isValid}>
                  {pending ? <Icon icon="loading" /> : mode === 'replay' ? 'Replay all' : 'Retry all'}
                </Button.Permission>
              </Tooltip>
            </Div>
          </StyledCardSection>
        </>}
    </Formik>;
};
export default SubmitBulkRequestRetryReplay;